import { useEffect, useState, useCallback } from 'react';
import NotificationManager, { NotificationOptions } from '../utils/notifications/NotificationManager';
import PushSubscriptionManager from '../utils/notifications/PushSubscriptionManager';
import ServiceWorkerHealthCheck from '../utils/notifications/ServiceWorkerHealthCheck';

interface UseNotificationsReturn {
  isSupported: boolean;
  isEnabled: boolean;
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  showNotification: (options: NotificationOptions) => Promise<void>;
  subscribe: (userId: string) => Promise<void>;
  unsubscribe: () => Promise<void>;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [isSupported] = useState(() => NotificationManager.isNativeSupported());
  const [isEnabled, setIsEnabled] = useState(() => NotificationManager.isNativeEnabled());
  const [permission, setPermission] = useState<NotificationPermission>(
    () => NotificationManager.getPermission()
  );

  useEffect(() => {
    PushSubscriptionManager.initialize();
    ServiceWorkerHealthCheck.startMonitoring();

    // Écouter les messages du Service Worker (logs en dev uniquement)
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_LOG' && process.env.NODE_ENV === 'development') {
        console.log(`[SW] ${event.data.message}`, event.data.data);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      ServiceWorkerHealthCheck.stopMonitoring();
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    const result = await NotificationManager.requestPermission();
    setPermission(result);
    setIsEnabled(result === 'granted');
    return result;
  }, []);

  const showNotification = useCallback(async (options: NotificationOptions): Promise<void> => {
    await NotificationManager.show(options);
  }, []);

  const subscribe = useCallback(async (userId: string): Promise<void> => {
    const actualPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    if (actualPermission !== 'granted') {
      console.warn('[PUSH] Permission non accordée');
      return;
    }
    await PushSubscriptionManager.subscribe(userId);
  }, []);

  const unsubscribe = useCallback(async (): Promise<void> => {
    await PushSubscriptionManager.unsubscribe();
  }, []);

  return {
    isSupported,
    isEnabled,
    permission,
    requestPermission,
    showNotification,
    subscribe,
    unsubscribe
  };
};
